import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Undo2, Check } from 'lucide-react';
import { formatWithThousandSeparator } from '@/utils';

interface AdvanceStatCardsProps {
  inProgress: number;
  returned: number;
  delivered: number;
  threshold: number;
}

function Money({ value }: { value: number }) {
  const { t } = useTranslation();
  return (
    <bdi dir='ltr' style={{ fontVariantNumeric: 'tabular-nums' }}>
      {formatWithThousandSeparator(value)} {String(t('iqd'))}
    </bdi>
  );
}

function IconChip({ children, bg }: { children: ReactNode; bg: string }) {
  return (
    <span
      className='inline-flex h-[30px] w-[30px] items-center justify-center rounded-[9px]'
      style={{ background: bg }}
    >
      {children}
    </span>
  );
}

/**
 * Stat cards row (DESIGN State A §4) — grid 1fr / 1fr / 1.4fr. The third card
 * carries the repayment-threshold progress bar (green fill, clamped 0–100%).
 */
function AdvanceStatCards({ inProgress, returned, delivered, threshold }: AdvanceStatCardsProps) {
  const { t } = useTranslation();
  const pct = threshold > 0 ? Math.min(100, Math.max(0, (delivered / threshold) * 100)) : 0;

  return (
    <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_1.4fr]'>
      {/* In-progress orders */}
      <div className='rounded-2xl border border-solid border-[#EBEBE6] bg-white px-[22px] py-5'>
        <div className='flex items-center gap-2.5 text-[#7A7A72]'>
          <IconChip bg='#EAF0F9'>
            <Sun className='h-4 w-4' style={{ color: '#2563A8' }} strokeWidth={2} />
          </IconChip>
          <span className='text-[13px] font-semibold'>{String(t('advance_cod_in_progress_orders'))}</span>
        </div>
        <div className='mt-3.5 text-[26px] font-extrabold tracking-[-0.6px] text-[#1B1B18]'>
          <Money value={inProgress} />
        </div>
        <div className='mt-1.5 text-[12px] text-[#9A9A92]'>
          {String(t('advance_cod_in_progress_caption'))}
        </div>
      </div>

      {/* Returned orders */}
      <div className='rounded-2xl border border-solid border-[#EBEBE6] bg-white px-[22px] py-5'>
        <div className='flex items-center gap-2.5 text-[#7A7A72]'>
          <IconChip bg='#FBEAE7'>
            <Undo2 className='h-4 w-4' style={{ color: '#C0392B' }} strokeWidth={2} />
          </IconChip>
          <span className='text-[13px] font-semibold'>{String(t('advance_cod_returned_orders'))}</span>
        </div>
        <div className='mt-3.5 text-[26px] font-extrabold tracking-[-0.6px] text-[#1B1B18]'>
          <Money value={returned} />
        </div>
        <div className='mt-1.5 text-[12px] text-[#9A9A92]'>
          {String(t('advance_cod_returned_caption'))}
        </div>
      </div>

      {/* Delivered toward next repayment */}
      <div className='rounded-2xl border border-solid border-[#EBEBE6] bg-white px-[22px] py-5'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2.5 text-[#7A7A72]'>
            <IconChip bg='#E7F1EA'>
              <Check className='h-4 w-4' style={{ color: '#15794B' }} strokeWidth={2} />
            </IconChip>
            <span className='text-[13px] font-semibold'>
              {String(t('advance_cod_delivered_toward_repayment'))}
            </span>
          </div>
          <span className='whitespace-nowrap text-[12px] font-semibold text-[#9A9A92]'>
            {String(t('advance_cod_threshold_label'))} <Money value={threshold} />
          </span>
        </div>
        <div className='mt-3.5 flex items-baseline gap-2'>
          <span className='text-[26px] font-extrabold tracking-[-0.6px] text-[#1B1B18]'>
            <Money value={delivered} />
          </span>
          <span className='text-[14px] font-semibold text-[#9A9A92]'>
            / <Money value={threshold} />
          </span>
        </div>
        <div className='mt-3 h-[9px] overflow-hidden rounded-md bg-[#EFEFEA]'>
          <div
            className='h-full rounded-md'
            style={{ width: `${pct}%`, background: '#15794B' }}
            data-testid='advance-cod-threshold-bar'
          />
        </div>
        <div className='mt-2 text-[12px] text-[#9A9A92]'>
          {String(t('advance_cod_threshold_footnote'))}
        </div>
      </div>
    </div>
  );
}

export default AdvanceStatCards;
